import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react";

import { Autocomplete, Backdrop, TextField, TextFieldProps } from "@mui/material";
import { debounce } from "@mui/material/utils";

// import { Loading } from "components";

interface AsyncAutocompleteProps extends Pick<TextFieldProps, "sx" | "className" | "label" | "placeholder" | "required"> {
  defaultValue?: string;
  value?: string;
  loading?: boolean;
  options?: string[];
  fetchData: (search: string) => any;
  onChange: (item: string) => void;
  disabled?: boolean;
  error?: boolean;
  helperText?: ReactNode;
}

export const AsyncAutocomplete: React.FC<AsyncAutocompleteProps> = ({
  defaultValue = "",
  value,
  loading = false,
  options = [],
  fetchData,
  onChange,
  sx = {
    marginBottom: 0,
    width: "100%",
    ".MuiOutlinedInput-root": {
      input: {
        paddingTop: "8px",
        paddingBottom: "8px"
      }
    }
  },
  error,
  helperText,
  ...props
}) => {
  const [inputValue, setInputValue] = useState<string>(value || defaultValue);
  const [open, setOpen] = useState(false);
  const autocompleteRef = useRef<HTMLDivElement>(null);
  const debounceFetchData = useMemo(
    () => debounce((search: string) => open && fetchData(search), 300),
    [fetchData, open]
  );

  // sync inputValue with value
  useEffect(() => {
    if (value !== undefined) {
      setInputValue(value);
    }
  }, [value]);

  const handleOnChange = (newValue: string) => {
    if (newValue !== inputValue) {
      setInputValue(newValue);
      onChange(newValue);
    }
  };

  return (
    <>
      {/* backdrop to block scrolling when dropdown is open */}
      <Backdrop
        open={open}
        sx={{
          zIndex: 1300,
          backgroundColor: "transparent"
        }}
        onClick={() => {
          setOpen(false);
        }}
      />
      <Autocomplete
        loading={loading}
        ref={autocompleteRef}
        fullWidth
        freeSolo
        disableClearable
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        options={options}
        value={inputValue}
        defaultValue={defaultValue}
        disabled={props.disabled}
        onChange={(_, newValue) => {
          if (newValue !== null) {
            handleOnChange(newValue);
          }
        }}
        onInputChange={(_, newInputValue) => {
          handleOnChange(newInputValue);
          debounceFetchData(newInputValue);
        }}
        onFocus={() => {
          debounceFetchData("");
        }}
        sx={sx}
        slotProps={{
          popper: {
            sx: {
              zIndex: 1300,
              "& .MuiAutocomplete-listbox": {
                maxHeight: "28vh",
                "& .MuiAutocomplete-option[aria-disabled='true']": {
                  opacity: 1,
                  "&.Mui-focused": {
                    opacity: 1
                  }
                },
                "& .MuiAutocomplete-option[aria-selected='true']": {
                  bgcolor: "#edf1f9",
                  "&.Mui-focused": {
                    bgcolor: "#edf1f9"
                  }
                },
                "& .MuiAutocomplete-option:hover": {
                  backgroundColor: "#edf1f9 !important"
                },
                "& .MuiAutocomplete-option.Mui-focused": {
                  bgcolor: "#edf1f9"
                }
              }
            }
          }
        }}
        renderInput={(params) => (
          <TextField
            onChange={(e) => {
              setInputValue(e.target.value);
            }}
            {...params}
            {...props}
            error={error}
            helperText={helperText}
            autoComplete="off"
          />
        )}
      />
    </>
  );
};
